import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Check, Phone, Smartphone, PhoneCall, Globe, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { coverage, TYPE_LABEL } from "@/data/coverage";
import { NUMBERS } from "@/data/pageCopy";

const { kicker, h1Lines, lead } = NUMBERS;

const TABS = [
  { key: "india", label: "India" },
  { key: "usa", label: "United States" },
  { key: "global", label: "Global coverage" },
];

const FILTERS = [
  { key: "all", label: "All types" },
  { key: "local", label: "Local" },
  { key: "mobile", label: "Mobile" },
  { key: "tollfree", label: "Toll-free" },
  { key: "uifn", label: "UIFN" },
];

const TYPE_ICON = { local: Phone, mobile: Smartphone, tollfree: PhoneCall, uifn: Globe };

function NumberTypeCard({ icon: Icon, name, prefix, desc, points }) {
  return (
    <div className="relative h-full rounded-xl border border-white/10 bg-[#0d0d0f] p-7 flex flex-col overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 90% 55% at 50% 0%, rgba(255,150,40,0.22), rgba(255,150,40,0) 70%)" }}
      />
      <div className="relative flex items-center justify-between">
        <Icon className="text-[#5B8CFF]" size={26} />
        {prefix && (
          <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">{prefix}</span>
        )}
      </div>
      <h3 className="relative mt-5 font-heading font-semibold text-lg text-white">{name}</h3>
      <p className="relative mt-2 text-neutral-400 text-sm leading-relaxed">{desc}</p>
      <ul className="relative mt-5 space-y-2">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2.5 text-sm text-neutral-300">
            <Check size={14} className="mt-0.5 shrink-0 text-[#5B8CFF]" />
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

function IndiaPanel() {
  return (
    <div>
      <div className="grid md:grid-cols-3 gap-6">
        <NumberTypeCard
          icon={Phone}
          name="Local numbers"
          prefix="Geographic DID"
          desc="City-code VoIP numbers your customers recognise as local — Mumbai, Delhi, Bengaluru, Chennai, Hyderabad, Pune, Kolkata and more."
          points={["Inbound calling on your city code", "Route to extensions, IVR, queues or an AI agent", "Add numbers in more cities as you grow"]}
        />
        <NumberTypeCard
          icon={Smartphone}
          name="Mobile-series numbers"
          prefix="10-digit"
          desc="VoIP numbers in the mobile ranges, for businesses whose customers expect a mobile format rather than a landline. No SIM and no handset — the number lands in your PBX like any other DID."
          points={["Familiar mobile format for customers", "Same routing and recording as any DID", "Answered in the browser softphone or by an AI agent"]}
        />
        <NumberTypeCard
          icon={PhoneCall}
          name="Toll-free"
          prefix="1800"
          desc="A 1800 number so customers across India can call you without paying for the call."
          points={["National reach on one number", "Calls are free for the caller", "Pairs well with an AI agent answering 24/7"]}
        />
      </div>

      <div className="mt-6 border border-white/10 bg-white/[0.03] px-6 py-5">
        <p className="text-sm text-neutral-300">
          Indian numbering is regulated — some number types need business documentation and operator approval before they
          can be issued. We confirm exactly what's needed for your use case during setup, before you commit.
        </p>
      </div>
    </div>
  );
}

function UsaPanel() {
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-6">
        <NumberTypeCard
          icon={Phone}
          name="Local DIDs"
          prefix="Any area code"
          desc="A local presence in any US city — pick the area code your customers already dial. Delivered over SIP, not as a SIM."
          points={["Nationwide area-code coverage", "Multiple numbers routed to one PBX", "Number porting supported"]}
        />
        <NumberTypeCard
          icon={PhoneCall}
          name="Toll-free"
          prefix="800 · 888 · 877 · 866 · 855 · 844 · 833"
          desc="Every active US toll-free prefix, for businesses that want a national, free-to-call number."
          points={["All seven toll-free prefixes available", "Free for the caller, nationwide", "Toll-free porting supported"]}
        />
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-white/[0.03] px-6 py-5">
          <p className="font-mono-t text-[10px] uppercase tracking-widest text-[#5B8CFF] mb-2">Also available</p>
          <p className="text-sm text-neutral-300">
            US mobile-series numbers and UIFN (Universal International Freephone) are both available alongside local and
            toll-free — all of them VoIP numbers on your PBX, not SIM lines.
          </p>
        </div>
        <div className="border border-white/10 bg-white/[0.03] px-6 py-5">
          <p className="font-mono-t text-[10px] uppercase tracking-widest text-[#5B8CFF] mb-2">Bring your own</p>
          <p className="text-sm text-neutral-300">
            Already have US numbers with another carrier? Keep them — connect your own trunk (BYOC) and use OwnPBX as the
            platform layer on top.
          </p>
        </div>
      </div>
    </div>
  );
}

function GlobalPanel() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return coverage.filter(
      (row) =>
        (filter === "all" || row.t.includes(filter)) &&
        (!needle || row.c.toLowerCase().includes(needle))
    );
  }, [q, filter]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a country…"
            data-testid="coverage-search"
            className="w-full pl-10 pr-4 py-3 border border-white/15 bg-white/[0.03] text-white text-sm focus:outline-none focus:border-white transition-colors"
          />
        </div>
        <div className="inline-flex flex-wrap border border-white/15">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors ${
                filter === f.key ? "bg-white text-neutral-900" : "bg-transparent text-neutral-400 hover:bg-white/5"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-neutral-500 mb-5" data-testid="coverage-count">
        Showing <span className="text-white font-semibold">{list.length}</span> of {coverage.length} countries
      </p>

      {list.length === 0 ? (
        <div className="border border-white/10 bg-white/[0.02] px-6 py-12 text-center text-neutral-400 text-sm">
          No country matches that search. Tell us what you need and we'll check availability directly with our carrier.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {list.map((row) => (
            <div key={row.c} className="bg-[#0a0a0a] px-5 py-4 hover:bg-white/[0.03] transition-colors">
              <p className="font-semibold text-sm text-white">{row.c}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {row.t.map((t) => {
                  const Icon = TYPE_ICON[t];
                  return (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[11px] text-neutral-300"
                    >
                      <Icon size={11} className="text-[#5B8CFF]" />
                      {TYPE_LABEL[t]}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NumbersPage() {
  const [tab, setTab] = useState("india");

  return (
    <div data-testid="numbers-page">
      <section className="relative pt-32 md:pt-40 pb-10 overflow-hidden bg-[#0a0a0a]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div
          className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-[1100px] h-[600px]"
          style={{
            background: "radial-gradient(ellipse 50% 50% at 50% 0%, rgba(0,71,255,0.35), rgba(0,71,255,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div
          className="pointer-events-none absolute left-1/2 top-[280px] -translate-x-1/2 w-[900px] h-[380px]"
          style={{
            background: "radial-gradient(ellipse 50% 50% at 50% 100%, rgba(255,130,40,0.18), rgba(255,130,40,0) 70%)",
            filter: "blur(50px)",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-5">{kicker}</p>
          </Reveal>
          <h1 className="font-heading font-medium tracking-tight leading-[0.98] text-4xl md:text-6xl lg:text-7xl text-white max-w-4xl">
            <MaskedLines lines={h1Lines} delay={0.1} />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 text-lg text-neutral-400 max-w-2xl leading-relaxed">
              {lead}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 bg-[#0a0a0a] border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="inline-flex flex-wrap border border-white/15 mb-10" data-testid="numbers-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`numbers-tab-${t.key}`}
                className={`px-5 py-2.5 text-sm font-semibold transition-colors ${
                  tab === t.key ? "bg-[#5B8CFF] text-white" : "bg-transparent text-neutral-400 hover:bg-white/5"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "india" && <IndiaPanel />}
          {tab === "usa" && <UsaPanel />}
          {tab === "global" && <GlobalPanel />}
        </div>
      </section>

      <section className="relative py-20 bg-[#0a0a0a] border-t border-white/10 overflow-hidden">
        <div
          className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-[1100px] h-[400px]"
          style={{
            background: "radial-gradient(ellipse 50% 50% at 50% 0%, rgba(255,150,40,0.2), rgba(255,150,40,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl text-white">
              Need a number in a specific country?
            </h2>
            <p className="mt-3 text-neutral-400 max-w-xl leading-relaxed">
              Tell us the country and number type you need and we'll confirm availability, documentation and pricing
              before you commit.
            </p>
          </div>
          <Link
            to="/demo"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white text-black font-bold px-8 py-4 hover:bg-neutral-200 transition-colors"
            data-testid="numbers-cta"
          >
            Talk to us <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
